import { HitsiTunnus } from "./HitsiTunnus";
import { useLataus, LatausTila } from "./Lataus";
import { useKohdeSynkka } from "./Kohdekonteksti";
import VtMerkki from "./VtMerkki";
import { jarjestaHitsit } from "./hitsijarjestys";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type Hitsi, type Kayttaja, type Kohde, type Punch } from "./api";
import LinjaTarkastus from "./LinjaTarkastus";
import IsometriMerkinnat from "./IsometriMerkinnat";
import { Tilasana } from "./Tila";
import { naytaPvm } from "./pvm";
import { t, virheTeksti, kieli } from "./kieli";

/* Toni 15.10.2026 klo 18.35: master-maininnat vain master-adapterin instanssissa (pekilo). Asetetaan näkymän piirrossa. */
let MASTER = false;

/* Valmiuden syyt tulevat kannasta (v_linja_tarkastusvalmius, 203) suomeksi seitsemänä kiinteänä muotona. Käännös tehdään
   tässä muodon mukaan eikä kantaan: näkymä on yksi totuus, ja tuntematon muoto näkyy sellaisenaan eikä katoa. */
const SYYT: [RegExp, (m: RegExpMatchArray) => string][] = [
  [/^jo tarkastettu$/, () => t("nippu.syy_tarkastettu")],
  [/^linjalla ei hitsejä$/, () => t("nippu.syy_ei_hitseja")],
  [/^(\d+) hitsiä hitsaamatta$/, (m) => t("nippu.syy_hitsaamatta", { n: m[1]! })],
  [/^(\d+) numeroton rivi, jossa päivä tai hitsaaja — korjattava masterissa$/, (m) => t(MASTER ? "nippu.syy_numeroton" : "nippu.syy_numeroton_yleinen", { n: m[1]! })],
  [/^(\d+) avointa A-punchia$/, (m) => t("nippu.syy_a_punchit", { n: m[1]! })],
  [/^(\d+) estävää puutetta$/, (m) => t("nippu.syy_estavat", { n: m[1]! })],
  [/^RT-korjaus avoin, hitsi (.+)$/, (m) => t("nippu.syy_rt", { hitsit: m[1]! })],
];
export function syyKielella(s: string): string {
  if (kieli() === "fi") return s;
  for (const [re, f] of SYYT) { const m = s.match(re); if (m) return f(m); }
  return s;
}

/* Päätös 90 — linjatarkastusnippu (pääChatin toimeksianto Tonin välittämänä 6.10.2026; Tonin omat tarkennukset:
   NDT ei ehto, isometrimerkki ja -suodatin, Sampo isäntä). Tarkastaja ottaa nipun valmiita linjoja eikä kentällä
   tarvitse etsiä seuraavaa. Valmius tulee yhdestä paikasta (v_linja_tarkastusvalmius); nippu on pelkkä työlista.
   Työtilassa välilehdet (Tarkastus · Isometri · Hitsit) pysyvät asennettuina, joten lomakkeen tila säilyy. */

type ValmisRivi = { linja_id: number; tunnus: string; alue: string | null; valmis: boolean; syyt: string[]; isometri: boolean;
  nipussa: boolean; tarkastuksen_tila: string | null; aloittamattomia_paikanpitajia?: number; vt_puuttuu?: number; vt_ilman_tulosta?: number };
/* T642 kohta 1 (Toni 20.10.2026 klo 16.50): nipun tila johdetaan tarkastuksesta — hyväksytty · ohitettu · kesken. Tallenna ei merkitse
   linjaa tehdyksi (päätös 90:n kasitelty-merkintä); «tehty» = hyväksytty. */
type Nipputila = "hyvaksytty" | "ohitettu" | "kesken";
type NippuRivi = { linja_id: number; jarjestys: number; kasitelty: string | null; ohitettu: string | null; tunnus: string;
  alue: string | null; valmis: boolean; syyt: string[]; isometri: boolean; tarkastuksen_tila: string | null; aloittamattomia_paikanpitajia?: number;
  vt_puuttuu?: number; vt_ilman_tulosta?: number; nipputila?: Nipputila };
type Nippu = { rivit: NippuRivi[]; tehty: number; ohitettuja?: number; yhteensa: number };
/** Vanha API ilman nipputilaa: luetaan samasta säännöstä (hyväksytty voittaa ohitetun). */
const nipputila = (r: NippuRivi): Nipputila => r.nipputila ?? (r.tarkastuksen_tila === "hyvaksytty" ? "hyvaksytty" : r.ohitettu ? "ohitettu" : "kesken");

/* 200 (sääntö A, Toni 6.10.2026 «Coworkin suositukset ok»): masterin numeroton «EI ALOITETTU» -rivi ei estä nippua, mutta
   linja merkitään — 21.9.:n linjaus «paikanpitäjä = linjalla voi olla kirjaamattomia hitsejä» näkyy tarkastajalle kentällä. */
function Aloittamaton({ n }: { n?: number }) {
  if (!n) return null;
  return <span className="huom" data-aloittamaton-rivi={n} style={{ display: "block", margin: "2px 0 0" }}>{t(MASTER ? "nippu.aloittamaton" : "nippu.aloittamaton_yleinen")}</span>;
}

const SAA = ["paakayttaja", "suunnittelija", "tyonjohtaja", "tarkastaja"];

export default function Tarkastusnippu({ kayttaja, osoiteKohde, asetaOsoiteKohde }: {
  kayttaja: Kayttaja; osoiteKohde: string | null; asetaOsoiteKohde: (s: string | null) => void;
}) {
  const saa = kayttaja.roolit.some((r) => SAA.includes(r));
  MASTER = !!kayttaja.master_adapteri;
  const [valilehti, setValilehti] = useState<"valmiit" | "nippu">("nippu");
  const [nippu, setNippu] = useState<Nippu | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const lataaNippu = useCallback(async () => {
    try { setNippu(await api.tarkastusnippu()); } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.lataus_virhe")); }
  }, []);
  useEffect(() => { void lataaNippu(); }, [lataaNippu]);
  useEffect(() => { if (nippu && nippu.yhteensa === 0) setValilehti("valmiit"); }, [nippu]);

  const linjaId = osoiteKohde && /^\d+$/.test(osoiteKohde) ? Number(osoiteKohde) : null;
  if (linjaId && nippu) {
    return <Tyotila linjaId={linjaId} nippu={nippu} saa={saa} sharepointissa={kayttaja.linjatarkastus_sharepointissa} oletusTarkastaja={kayttaja.nimi ?? undefined} paivita={lataaNippu} avaa={(id) => asetaOsoiteKohde(id ? String(id) : null)} />;
  }

  return (
    <>
      <header className="head">
        <div className="head__rivi"><h1>{t("valikko.linjatarkastus")}</h1></div>
      </header>
      <div className="isomerkinta__pohjat" role="tablist" style={{ margin: "12px 0" }}>
        <button role="tab" aria-selected={valilehti === "nippu"} className={`nappi nappi--kevyt${valilehti === "nippu" ? " nappi--valittu" : ""}`}
          data-nippu-valilehti="nippu" onClick={() => setValilehti("nippu")}>
          {t("nippu.minun")}{nippu ? ` (${nippu.tehty}/${nippu.yhteensa})` : ""}
        </button>
        <button role="tab" aria-selected={valilehti === "valmiit"} className={`nappi nappi--kevyt${valilehti === "valmiit" ? " nappi--valittu" : ""}`}
          data-nippu-valilehti="valmiit" onClick={() => setValilehti("valmiit")}>{t("nippu.valmiit")}</button>
      </div>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {valilehti === "valmiit"
        ? <Valmiit saa={saa} otettu={() => { void lataaNippu(); setValilehti("nippu"); }} />
        : <MinunNippuni nippu={nippu} avaa={(id) => asetaOsoiteKohde(String(id))} paivita={lataaNippu} />}
    </>
  );
}

function Valmiit({ saa, otettu }: { saa: boolean; otettu: () => void }) {
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [kohde, setKohde] = useState<number | "">("");
  // T426: yläpalkin kohde ohjaa. «Kaikki kohteet» ei kelpaa tälle näkymälle, joten silloin näkymä pitää oman oletuksensa
  // ja valitsimensa eikä työnnä arvaustaan yläpalkkiin.
  const { ylapalkissa, ylaValittu } = useKohdeSynkka(kohde, setKohde, { kaikkiSallittu: false });
  const [alue, setAlue] = useState(""); const [haku, setHaku] = useState("");
  const [vainValmiit, setVainValmiit] = useState(true);
  const [isometri, setIsometri] = useState<"kaikki" | "on" | "ei">("kaikki");
  const [data, setData] = useState<{ kohde: number; rivit: ValmisRivi[]; valmiita: number; valmiita_isometrilla: number; alueet: string[] } | null>(null);
  const [valitut, setValitut] = useState<Set<number>>(new Set());
  const [viesti, setViesti] = useState<string | null>(null);

  const klKohteet = useLataus();
  useEffect(() => { klKohteet.seuraa(api.kohteet().then((k) => { setKohteet(k); setKohde((v) => v === "" && k.length ? (k.find((x) => (x.linjoja ?? 0) > 0) ?? k[0]!).id : v); })); }, [klKohteet.kerta]);
  // T465 (10.10.2026): hakukenttä ei lähetä kyselyä jokaisesta merkistä. Mitattu demossa: «2», «20», «200», «200-» …
  // lähtivät kukin omana kutsunaan, ja rinnakkaiset raskaat kyselyt tukkivat palvelimen. Haku lähtee 400 ms viimeisen
  // näppäilyn jälkeen; Enter ei lähetä mitään erikseen (kenttä ei ole lomakkeessa).
  const [hakuViive, setHakuViive] = useState(haku);
  useEffect(() => { const ajastin = setTimeout(() => setHakuViive(haku), 400); return () => clearTimeout(ajastin); }, [haku]);
  useEffect(() => {
    if (kohde === "") return;
    let elossa = true;
    setViesti(null);
    api.valmiitTarkastukseen({ kohde, alue: alue || undefined, haku: hakuViive || undefined, vain_valmiit: vainValmiit ? "1" : "0", isometri })
      .then((d) => { if (elossa) { setData({ ...d, kohde }); setValitut(new Set()); } })
      .catch((e) => { if (elossa) setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.haku_virhe")); });
    return () => { elossa = false; };
  }, [kohde, alue, hakuViive, vainValmiit, isometri]);

  const valittavat = (data?.rivit ?? []).filter((r) => r.valmis && !r.nipussa);
  async function ota() {
    try {
      const r = await api.lisaaNippuun([...valitut]);
      setViesti(t("nippu.lisatty", { n: r.lisatty })); otettu();
    } catch (e) { setViesti(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.lisays_virhe")); }
  }

  return (
    <section className="panel" data-valmiit-tarkastukseen="">
      <LatausTila l={klKohteet} nimi="lataus.kohteet" />
      <div className="selailu__suodattimet" style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        {!(ylapalkissa && ylaValittu != null) && <label className="dim">{t("kuittaus.kohde")}<br />
          <select value={kohde} onChange={(e) => { setKohde(Number(e.target.value)); setAlue(""); }}>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select></label>}
        <label className="dim">{t("kuittaus.alue")}<br />
          <select value={alue} onChange={(e) => setAlue(e.target.value)} data-suodatin="alue">
            <option value="">{t("nippu.kaikki")}</option>
            {(data?.alueet ?? []).sort().map((a) => <option key={a} value={a}>{a}</option>)}
          </select></label>
        <label className="dim">{t("nippu.linja")}<br /><input value={haku} onChange={(e) => setHaku(e.target.value)} placeholder={t("nippu.haku_esim")} style={{ width: 340, maxWidth: "100%" }} /></label>
        <label className="dim">{t("nippu.isometri")}<br />
          <select value={isometri} onChange={(e) => setIsometri(e.target.value as "kaikki" | "on" | "ei")} data-suodatin="isometri">
            <option value="kaikki">{t("nippu.kaikki")}</option><option value="on">{t("nippu.on_isometri")}</option><option value="ei">{t("nippu.ei_isometria")}</option>
          </select></label>
        <label className="dim" style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={vainValmiit} onChange={(e) => setVainValmiit(e.target.checked)} /> {t("nippu.vain_valmiit")}</label>
      </div>
      {data && (
        <p data-valmiit-luku={data.valmiita} data-valmiit-kohde={data.kohde} style={{ margin: "8px 0" }}>
          {t("nippu.valmiita")} <b>{data.valmiita}</b>, {t("nippu.joista_isometri")} <b>{data.valmiita_isometrilla}</b>.
          {" "}{t("nippu.valmis_maaritelma")}
        </p>
      )}
      {viesti && <p className="dim" role="status">{viesti}</p>}
      {saa && valittavat.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "6px 0" }}>
          <button className="nappi nappi--kevyt" data-valitse-kaikki="" onClick={() => setValitut(new Set(valittavat.map((r) => r.linja_id)))}>
            {t("nippu.valitse_kaikki", { n: valittavat.length })}</button>
          <button className="nappi nappi--paa" disabled={valitut.size === 0} data-ota-nippu="" onClick={() => void ota()}>
            {t("nippu.ota", { n: valitut.size })}</button>
          {valitut.size === 0 && <span className="este-syy" data-este-syy="">{t("nippu.syy_ei_valittuja")}</span>}
        </div>
      )}
      <div className="scroll">
        <table className="tbl" style={{ fontSize: 13 }}>
          <thead><tr><th></th><th>{t("nippu.linja")}</th><th>{t("kuittaus.alue")}</th><th>{t("nippu.isometri")}</th><th>{t("nippu.tila")}</th></tr></thead>
          <tbody>
            {(data?.rivit ?? []).map((r) => (
              <tr key={r.linja_id} data-valmis-linja={r.linja_id}>
                <td>{saa && r.valmis && !r.nipussa
                  ? <input type="checkbox" aria-label={t("nippu.valitse_linja", { tunnus: r.tunnus })} checked={valitut.has(r.linja_id)}
                      onChange={(e) => setValitut((v) => { const n = new Set(v); if (e.target.checked) n.add(r.linja_id); else n.delete(r.linja_id); return n; })} />
                  : r.nipussa ? <span className="dim">{t("nippu.nipussa")}</span> : null}</td>
                <td className="mono">{r.tunnus}<Aloittamaton n={r.aloittamattomia_paikanpitajia} /><VtMerkki n={r.vt_puuttuu} ilmanTulosta={r.vt_ilman_tulosta} /></td>
                <td>{r.alue ?? "–"}</td>
                <td data-isometri-merkki={r.isometri ? "on" : "ei"}>{r.isometri ? <Tilasana laji="kunnossa">{t("nippu.on")}</Tilasana> : <Tilasana laji="ei_mitattu">{t("nippu.ei")}</Tilasana>}</td>
                <td>{r.valmis ? <Tilasana laji="kunnossa">{t("nippu.valmis")}</Tilasana> : <span className="dim">{r.syyt.map(syyKielella).join(" · ")}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function MinunNippuni({ nippu, avaa, paivita }: { nippu: Nippu | null; avaa: (id: number) => void; paivita: () => void }) {
  if (!nippu) return <p className="dim">{t("ltar.ladataan")}</p>;
  if (nippu.yhteensa === 0) return <p className="dim" data-nippu-tyhja="">{t("nippu.tyhja")}</p>;
  // «Kaikki käsitelty» vain, kun jokainen linja on hyväksytty tai ohitettu (T642).
  const seuraava = nippu.rivit.find((r) => nipputila(r) === "kesken");
  const ohitettuja = nippu.rivit.filter((r) => nipputila(r) === "ohitettu").length;
  return (
    <section className="panel" data-minun-nippuni="">
      <p style={{ margin: "6px 0" }} data-nippu-edistyminen={`${nippu.tehty}/${nippu.yhteensa}`} data-nippu-ohitettuja={ohitettuja}>
        {t("nippu.hyvaksyttyja")} <b>{nippu.tehty} / {nippu.yhteensa}</b>{ohitettuja > 0 ? ` · ${t("nippu.ohitettuja", { n: ohitettuja })}` : ""}
        {seuraava ? <> · {t("nippu.seuraava_pieni")} <b className="mono">{seuraava.tunnus}</b></> : ` · ${t("nippu.kaikki_kasitelty")}`}
      </p>
      {seuraava && <button className="nappi nappi--paa" data-nippu-aloita="" onClick={() => avaa(seuraava.linja_id)}>{t("nippu.avaa_seuraava")}</button>}
      <ol className="isomerkinta__lista" style={{ marginTop: 10 }}>
        {nippu.rivit.map((r) => (
          <li key={r.linja_id} data-nippu-linja={r.linja_id}>
            <button className="tekstilinkki" onClick={() => avaa(r.linja_id)}>{r.tunnus}</button>
            {" "}<span data-nipputila={nipputila(r)}>{nipputila(r) === "hyvaksytty" ? <Tilasana laji="kunnossa">{t("nippu.hyvaksytty_pieni")}</Tilasana>
              : nipputila(r) === "ohitettu" ? <Tilasana laji="ei_mitattu">{t("nippu.ohitettu")}</Tilasana>
              : <Tilasana laji="varoitus">{t("nippu.kesken_pieni")}</Tilasana>}</span>
            {r.isometri ? <span className="dim"> · {t("nippu.isometri_pieni")}</span> : <span className="dim"> · {t("nippu.ei_isometria")}</span>}
            <Aloittamaton n={r.aloittamattomia_paikanpitajia} />
            <VtMerkki n={r.vt_puuttuu} ilmanTulosta={r.vt_ilman_tulosta} />
            {" "}<button className="tekstilinkki" onClick={() => void api.poistaNipusta(r.linja_id).then(paivita)} aria-label={t("nippu.poista_aria", { tunnus: r.tunnus })}>{t("nippu.poista")}</button>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Tyotila({ linjaId, nippu, saa, sharepointissa, oletusTarkastaja, paivita, avaa }: {
  linjaId: number; oletusTarkastaja?: string; nippu: Nippu; saa: boolean; sharepointissa?: boolean; paivita: () => Promise<void>; avaa: (id: number | null) => void;
}) {
  const [valilehti, setValilehti] = useState<"tarkastus" | "isometri" | "hitsit">("tarkastus");
  const i = nippu.rivit.findIndex((r) => r.linja_id === linjaId);
  const rivi = i >= 0 ? nippu.rivit[i]! : null;
  const edellinen = i > 0 ? nippu.rivit[i - 1]! : null;
  const seuraava = nippu.rivit.slice(i + 1).find((r) => nipputila(r) === "kesken") ?? null;
  async function ohita() {
    if (rivi) await api.merkitseNippu(linjaId, { ohitettu: true });
    await paivita();
    avaa(seuraava?.linja_id ?? null);
  }
  // T642 (Toni 20.10.2026 klo 16.50): Tallenna ei merkitse linjaa käsitellyksi eikä siirry — nippu päivittyy. Hyväksyntä siirtää seuraavaan.
  async function tallennettu(hyvaksytty: boolean) {
    await paivita();
    if (hyvaksytty) avaa(seuraava?.linja_id ?? null);
  }
  return (
    <div className="nipputyotila" data-nippu-tyotila={linjaId}>
      <div className="nipputyotila__ylapalkki">
        <button className="nappi nappi--kevyt" onClick={() => avaa(null)}>‹ {t("nippu.nippu")}</button>
        <span data-nippu-edistyminen={`${nippu.tehty}/${nippu.yhteensa}`}><b>{nippu.tehty}/{nippu.yhteensa}</b></span>
        <span className="mono" data-nippu-nykyinen={rivi?.tunnus ?? ""}>{rivi?.tunnus ?? t("nippu.linja_id", { id: linjaId })}</span>
      </div>
      <div className="nipputyotila__navi">
        <button className="nappi nappi--kevyt" disabled={!edellinen} data-nippu-edellinen="" onClick={() => edellinen && avaa(edellinen.linja_id)}>{t("nippu.edellinen")}</button>
        <button className="nappi nappi--kevyt" disabled={!saa || !rivi} data-nippu-ohita="" onClick={() => void ohita()}>{t("nippu.ohita")}</button>
        <button className="nappi nappi--kevyt" disabled={!seuraava} data-nippu-seuraava="" onClick={() => seuraava && avaa(seuraava.linja_id)}>{t("nippu.seuraava")}</button>
      </div>
      <div className="isomerkinta__pohjat" role="tablist">
        {(["tarkastus", "isometri", "hitsit"] as const).map((v) => (
          <button key={v} role="tab" aria-selected={valilehti === v} data-tyotila-valilehti={v}
            className={`nappi nappi--kevyt${valilehti === v ? " nappi--valittu" : ""}`} onClick={() => setValilehti(v)}>
            {v === "tarkastus" ? t("nippu.v_tarkastus") : v === "isometri" ? t("nippu.isometri") : t("nippu.v_hitsit")}
          </button>
        ))}
      </div>
      {/* Kaikki kolme pysyvät asennettuina (hidden), joten tila säilyy välilehteä vaihdettaessa. key = linja: linjan vaihto
          purkaa ne (päätös 89), jolloin edellisen linjan vastaus ei täytä uuden lomaketta. */}
      <div hidden={valilehti !== "tarkastus"} data-tyotila-osa="tarkastus">
        <LinjaTarkastus key={`t${linjaId}`} linjaId={linjaId} saaMuokata={saa} sharepointissa={sharepointissa} oletusTarkastaja={oletusTarkastaja} onTallennettu={(h) => void tallennettu(h)} />
      </div>
      <div hidden={valilehti !== "isometri"} data-tyotila-osa="isometri">
        <IsometriMerkinnat key={`i${linjaId}`} linjaId={linjaId} saaKirjata={saa} />
      </div>
      <div hidden={valilehti !== "hitsit"} data-tyotila-osa="hitsit">
        <LinjanHitsit key={`h${linjaId}`} linjaId={linjaId} />
      </div>
    </div>
  );
}

function LinjanHitsit({ linjaId }: { linjaId: number }) {
  const [hitsit, setHitsit] = useState<Hitsi[] | null>(null);
  const [hakuvirhe, setHakuvirhe] = useState<string | null>(null);
  const [punchit, setPunchit] = useState<Punch[]>([]);
  const [auki, setAuki] = useState<number | null>(null);
  useEffect(() => {
    let elossa = true;
    // Raja 500 = reitin maksimi (sama kuin Selailussa). 6.10.2026 tässä oli 2000 → 400, ja virhe näytti tyhjältä linjalta.
    api.hitsit(linjaId, 500).then((h) => { if (elossa) setHitsit(h); })
      .catch((e) => { if (elossa) setHakuvirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.hitsit_virhe")); });
    api.punchit(linjaId).then((p) => { if (elossa) setPunchit(p); }).catch(() => {});
    return () => { elossa = false; };
  }, [linjaId]);
  const avoimet = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of punchit) if (p.hitsi_id && !p.kuitannut && !p.kuittaus_pvm) m.set(p.hitsi_id, (m.get(p.hitsi_id) ?? 0) + 1);
    return m;
  }, [punchit]);
  // T641 (Toni 20.10.2026 klo 16.39): palvelin antaa uusimman ensin; lista on lehti- ja numerojärjestyksessä (yhteinen funktio).
  const jarjestetyt = useMemo(() => (hitsit ? jarjestaHitsit(hitsit) : null), [hitsit]);
  if (hakuvirhe) return <p role="alert" data-linjan-hitsit-virhe="">{hakuvirhe}</p>;
  if (!hitsit) return <p className="dim">{t("nippu.ladataan_hitseja")}</p>;
  if (hitsit.length === 0) return <p className="dim">{t("nippu.ei_hitseja")}</p>;
  const ndt = (h: Hitsi) => {
    const tk = h.tarkastukset ?? [];
    if (!tk.length) return t("nippu.ndt_ei");
    if (tk.some((x) => x.tulos === "hylatty")) return t("nippu.ndt_hylatty");
    if (tk.every((x) => x.tulos === "hyvaksytty")) return t("nippu.ndt_hyvaksytty");
    return t("nippu.ndt_kesken");
  };
  return (
    <div className="scroll" data-linjan-hitsit={hitsit.length}>
      <table className="tbl" style={{ fontSize: 13 }}>
        <thead><tr><th>{t("hl.hitsi")}</th><th>{t("nippu.tila")}</th><th>NDT</th><th>{t("nippu.avoin_punch")}</th></tr></thead>
        <tbody>
          {(jarjestetyt ?? hitsit).map((h) => (
            <Fragment key={h.id}>
              <tr data-hitsi-rivi={h.id} data-hitsinumero={h.hitsinumero} data-lehti={h.piirustuslehti ?? ""} onClick={() => setAuki(auki === h.id ? null : h.id)} style={{ cursor: "pointer" }}>
                <td className="mono"><HitsiTunnus numero={h.hitsinumero} lehti={h.piirustuslehti} /></td>
                <td>{h.vaihe === "hitsattu" ? t("nippu.hitsattu") : h.vaihe === "suunniteltu" ? t("nippu.suunniteltu") : h.vaihe}</td>
                <td>{ndt(h)}</td>
                <td>{avoimet.get(h.id) ? <Tilasana laji="vika">{t("pc.n_avoinna", { n: avoimet.get(h.id) ?? 0 })}</Tilasana> : "–"}</td>
              </tr>
              {auki === h.id && (
                <tr data-hitsi-kortti={h.id}><td colSpan={4}>
                  {/* Hitsin kortti ilman hitsaajien nimiä (päätös 90). */}
                  {t("hl.paiva")} {h.paiva ? naytaPvm(h.paiva) : "–"} · DN {h.dn ?? "–"} · {t("nippu.prosessi")} {h.hitsausprosessi ?? "–"} · WPS {h.wps ?? "–"}
                  {h.tarra ? ` · ${t("nippu.tarra")} ${h.tarra}` : ""}
                  {(h.tarkastukset ?? []).length > 0 && <> · {(h.tarkastukset ?? []).map((t) => `${t.menetelma} ${t.tulos}`).join(", ")}</>}
                </td></tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
